import React, { useState } from 'react';
import { api } from '../services/api';
import ItemFormModal from './ItemFormModal';

export default function CategoryDetail({ category, categories, onRefresh, isDarkMode }) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    const styles = getStyles(isDarkMode);

    const handleOpenAdd = () => {
        setEditingItem(null);
        setIsModalOpen(true);
    };

    const handleOpenEdit = (item) => {
        setEditingItem(item);
        setIsModalOpen(true);
    };

    const handleFormSubmit = async (formData) => {
        try {
            const data = new FormData();

            // Prioritize dropdown selection, fallback to current category ID if not virtual
            if (formData.categoryId) {
                data.append('categoryId', formData.categoryId);
            } else if (!editingItem && !category.isVirtual) {
                data.append('categoryId', category.id);
            }

            data.append('name', formData.name);
            data.append('price', formData.price);
            if (formData.offerPrice) data.append('offerPrice', formData.offerPrice);
            if (formData.description) data.append('description', formData.description);
            if (formData.image) data.append('image', formData.image);

            data.append('trackStock', formData.trackStock);
            if (formData.trackStock) data.append('stockQuantity', formData.stockQuantity);

            data.append('isAvailable', formData.isAvailable);
            if (formData.estimatedPrepTime) data.append('estimatedPrepTime', formData.estimatedPrepTime);

            if (formData.variants) {
                data.append('variants', JSON.stringify(formData.variants));
            }

            const config = { headers: { 'Content-Type': 'multipart/form-data' } };

            if (editingItem) {
                await api.put(`/restaurant/item/${editingItem.id}`, data, config);
            } else {
                await api.post('/restaurant/item', data, config);
            }

            setIsModalOpen(false);
            setEditingItem(null);
            onRefresh();
        } catch (error) {
            console.error(error);
            alert("Failed to save item: " + (error.response?.data?.error || error.message));
        }
    };

    const toggleAvailability = async (item, newVal) => {
        try {
            await api.put(`/restaurant/item/${item.id}`, { isAvailable: newVal });
            onRefresh();
        } catch (error) {
            console.error(error);
        }
    };

    const handleDeleteCategory = async () => {
        if (!window.confirm(`Are you sure you want to delete "${category.name}"? Items will be moved to Uncategorized.`)) return;
        try {
            await api.delete(`/restaurant/category/${category.id}`);
            onRefresh();
        } catch (error) {
            console.error(error);
            alert("Failed to delete category");
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div>
                    <h2 style={{ margin: 0 }}>{category.name}</h2>
                    <span style={{ fontSize: '0.9rem', color: isDarkMode ? '#aaa' : '#666' }}>{category.items?.length || 0} items</span>
                </div>
                <div style={styles.actions}>
                    {!category.isVirtual && (
                        <button onClick={handleDeleteCategory} style={{ ...styles.secondaryBtn, borderColor: '#D32F2F', color: '#D32F2F', marginRight: '10px' }}>
                            Delete Category
                        </button>
                    )}
                    <button onClick={handleOpenAdd} style={styles.primaryBtn}>+ Add Item</button>
                </div>
            </div>

            <ItemFormModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleFormSubmit}
                initialData={editingItem}
                categories={categories}
                currentCategoryId={category.isVirtual ? null : category.id}
                isDarkMode={isDarkMode}
            />

            <div style={styles.grid}>
                {category.items && category.items.length > 0 ? (
                    category.items.map(item => (
                        <div key={item.id} style={{ ...styles.card, opacity: item.isAvailable ? 1 : 0.7 }}>
                            <div style={styles.imageOverlayContainer}>
                                {item.imageUrl ? (
                                    <img src={item.imageUrl} alt={item.name} style={styles.itemImage} />
                                ) : (
                                    <div style={styles.noImagePlaceholder}>No Image</div>
                                )}
                                {!item.isAvailable && (
                                    <div style={styles.unavailableOverlay}>Unavailable</div>
                                )}
                                <div style={styles.overlayActions}>
                                    <button
                                        style={styles.editBtn}
                                        onClick={() => handleOpenEdit(item)}
                                        title="Edit Item"
                                    >
                                        ✏️
                                    </button>
                                </div>
                            </div>

                            <div style={styles.cardContent}>
                                <div style={styles.cardHeader}>
                                    <h4 style={styles.itemName}>{item.name}</h4>
                                    <div style={styles.priceContainer}>
                                        {item.offerPrice && item.offerPrice < item.price ? (
                                            <>
                                                <span style={styles.originalPrice}>₹{item.price}</span>
                                                <span style={styles.offerPrice}>₹{item.offerPrice}</span>
                                            </>
                                        ) : (
                                            <span style={styles.itemPrice}>₹{item.price}</span>
                                        )}
                                    </div>
                                </div>
                                {item.description && (
                                    <p style={styles.description}>{item.description}</p>
                                )}

                                <div style={styles.stockRow}>
                                    {item.trackStock && (
                                        <span style={{ ...styles.badge, backgroundColor: item.stockQuantity < 5 ? '#ffeaa7' : '#dfe6e9', color: item.stockQuantity < 5 ? '#d35400' : '#2d3436' }}>
                                            Stock: {item.stockQuantity}
                                        </span>
                                    )}
                                    {item.variants && item.variants.length > 0 && (
                                        <span style={{ ...styles.badge, backgroundColor: '#e3f2fd', color: '#1976d2', marginLeft: '6px' }}>
                                            {item.variants.length} Variants
                                        </span>
                                    )}
                                </div>

                                <div style={styles.cardFooter}>
                                    <label style={styles.switchLabel}>
                                        <span style={{ fontSize: '0.85rem', color: isDarkMode ? '#ccc' : '#555' }}>Available</span>
                                        <div style={styles.switchWrapper}>
                                            <input
                                                type="checkbox"
                                                checked={item.isAvailable}
                                                onChange={(e) => toggleAvailability(item, e.target.checked)}
                                                style={styles.checkboxInput}
                                            />
                                            <span style={{ ...styles.slider, backgroundColor: item.isAvailable ? '#00b894' : (isDarkMode ? '#555' : '#ccc') }}></span>
                                            <span style={{ ...styles.knob, transform: item.isAvailable ? 'translateX(16px)' : 'translateX(0)' }}></span>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div style={styles.emptyState}>
                        <p>No items in this category yet.</p>
                        <button onClick={handleOpenAdd} style={styles.secondaryBtn}>Add your first item</button>
                    </div>
                )}
            </div>
        </div >
    );
}

const getStyles = (isDark) => ({
    container: {
        padding: '24px',
        color: isDark ? '#fff' : '#2d3436',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
        paddingBottom: '16px',
        borderBottom: isDark ? '1px solid #444' : '1px solid #e0e0e0'
    },
    primaryBtn: {
        padding: '10px 24px',
        backgroundColor: '#6C5CE7',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontWeight: '600',
        fontSize: '0.9rem',
        boxShadow: '0 4px 6px rgba(108, 92, 231, 0.2)'
    },
    secondaryBtn: {
        padding: '8px 16px',
        marginTop: '10px',
        backgroundColor: 'transparent',
        border: '1px solid #6C5CE7',
        color: '#6C5CE7',
        borderRadius: '6px',
        cursor: 'pointer'
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: '24px'
    },
    card: {
        backgroundColor: isDark ? '#2d3436' : '#fff',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.08)',
        border: isDark ? '1px solid #444' : '1px solid #f0f0f0',
        transition: 'transform 0.2s',
        display: 'flex',
        flexDirection: 'column'
    },
    imageOverlayContainer: {
        position: 'relative',
        height: '180px',
        backgroundColor: isDark ? '#1e1e1e' : '#f5f5f5'
    },
    itemImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },
    noImagePlaceholder: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#aaa',
        fontSize: '0.9rem'
    },
    unavailableOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        backdropFilter: 'blur(2px)'
    },
    overlayActions: {
        position: 'absolute',
        top: '10px',
        right: '10px',
        zIndex: 2
    },
    editBtn: {
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        backgroundColor: 'white',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
        fontSize: '1rem'
    },
    cardContent: {
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        flex: 1
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '6px'
    },
    itemName: {
        margin: 0,
        fontSize: '1.1rem',
        fontWeight: 'bold',
        color: isDark ? '#fff' : '#2d3436',
        lineHeight: 1.3
    },
    priceContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        minWidth: '60px'
    },
    itemPrice: {
        fontWeight: '700',
        color: '#2d3436',
        fontSize: '1rem',
        color: isDark ? '#fff' : '#2d3436'
    },
    originalPrice: {
        textDecoration: 'line-through',
        fontSize: '0.8rem',
        color: '#b2bec3'
    },
    offerPrice: {
        fontWeight: '700',
        color: '#e74c3c',
        fontSize: '1rem'
    },
    description: {
        fontSize: '0.85rem',
        color: isDark ? '#b2bec3' : '#636e72',
        margin: '5px 0 10px 0',
        lineHeight: 1.4,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden'
    },
    stockRow: {
        marginTop: 'auto',
        marginBottom: '12px'
    },
    badge: {
        fontSize: '0.75rem',
        padding: '2px 8px',
        borderRadius: '12px',
        fontWeight: '600'
    },
    cardFooter: {
        borderTop: isDark ? '1px solid #444' : '1px solid #f0f0f0',
        paddingTop: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    switchLabel: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        cursor: 'pointer'
    },
    switchWrapper: {
        position: 'relative',
        width: '36px',
        height: '20px',
    },
    checkboxInput: {
        opacity: 0,
        width: 0,
        height: 0
    },
    slider: {
        position: 'absolute',
        cursor: 'pointer',
        top: 0, left: 0, right: 0, bottom: 0,
        borderRadius: '34px',
        transition: '.3s'
    },
    knob: {
        position: 'absolute',
        height: '16px',
        width: '16px',
        left: '2px',
        bottom: '2px',
        backgroundColor: 'white',
        borderRadius: '50%',
        transition: '.3s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
    },
    emptyState: {
        gridColumn: '1 / -1',
        padding: '60px',
        textAlign: 'center',
        color: isDark ? '#aaa' : '#888',
        backgroundColor: isDark ? '#2d3436' : '#f9f9f9',
        borderRadius: '12px',
        border: isDark ? '1px dashed #444' : '1px dashed #ccc'
    }
});
